import { api } from "@/lib/api-client"

export type FilthBackend = {
  id: string
  name: string
  base_url: string
  tenant_id?: string
  enabled: boolean
  method?: string
  default_scope?: string | null
  consumer_mode?: "off" | "shadow" | "primary"
  delegated_scope?: string | null
  company_wide?: boolean
  identity_headers?: Record<string, string>
  credential_ref?: { secret_name: string; vault_name?: string }
  created_at?: string
  updated_at?: string
}

export type CreateFilthBackendInput = {
  id: string
  name: string
  base_url: string
  tenant_id?: string
  method?: string
  default_scope?: string
  consumer_mode?: "off" | "shadow" | "primary"
  delegated_scope?: string
  company_wide?: boolean
  /** Inline bearer token, stored in Key Vault. Mutually exclusive with credential_secret_name. */
  token?: string
  /** Name of a pre-provisioned HAI_SECRET_<NAME> credential (file config backend only). */
  credential_secret_name?: string
  enabled?: boolean
}

export type UpdateFilthBackendInput = Partial<Omit<CreateFilthBackendInput, "id">>

export type TestFilthBackendResult = {
  success: boolean
  latency_ms?: number
  error?: string
  health?: { status?: string; db_status?: string; version?: string }
}

export type IntrospectFilthBackendResult = {
  success: boolean
  error?: string
  stats?: {
    file_count?: number
    dir_count?: number
    crawl_status?: string | null
    crawl_progress?: number | null
    text_embeddings_coverage?: number
    content_ready?: boolean
  }
}

export async function listFilthBackends(): Promise<FilthBackend[]> {
  const response = await api.get("/admin/filth-backends")
  const data = (response.data || response) as { backends: FilthBackend[] }
  return Array.isArray(data.backends) ? data.backends : []
}

export async function createFilthBackend(input: CreateFilthBackendInput): Promise<FilthBackend> {
  const response = await api.post("/admin/filth-backends", input)
  const data = (response.data || response) as { backend: FilthBackend }
  return data.backend
}

export async function updateFilthBackend(
  id: string,
  input: UpdateFilthBackendInput
): Promise<FilthBackend> {
  const response = await api.put(`/admin/filth-backends/${encodeURIComponent(id)}`, input)
  const data = (response.data || response) as { backend: FilthBackend }
  return data.backend
}

export async function deleteFilthBackend(id: string): Promise<void> {
  await api.delete(`/admin/filth-backends/${encodeURIComponent(id)}`)
}

export async function testFilthBackend(id: string): Promise<TestFilthBackendResult> {
  const response = await api.post(`/admin/filth-backends/${encodeURIComponent(id)}/test`, {})
  return (response.data || response) as TestFilthBackendResult
}

export async function introspectFilthBackend(id: string): Promise<IntrospectFilthBackendResult> {
  const response = await api.post(`/admin/filth-backends/${encodeURIComponent(id)}/introspect`, {})
  return (response.data || response) as IntrospectFilthBackendResult
}
